import { FC, useEffect, useRef } from "react";
// i18next
import { useTranslation } from "react-i18next";
// Icons
import { ChevronLeft, ChevronRight, Settings2, Bug } from 'lucide-react';

type Props = {
  topic: string;
  setTopic: (topic: string) => void;
  isAdvancedOpen: boolean;
  setIsAdvancedOpen: (open: boolean) => void;
  isDebugMode: boolean;
  setIsDebugMode: (open: boolean) => void;
  isScoringType: boolean;
  difficulty: string;
  setDifficulty: (difficulty: string) => void;
  questionCount: number | string;
  changeQuestionCount: (e: React.ChangeEvent<HTMLInputElement>) => void;
  questionTimeout: number | string;
  changeQuestionTimeout: (e: React.ChangeEvent<HTMLInputElement>) => void;
  questionScore: number | string;
  changeQuestionScore: (e: React.ChangeEvent<HTMLInputElement>) => void;
  editablePrompt: string;
  setEditablePrompt: (prompt: string) => void;
  defaultPrompt: string;
  onNext: () => void;
  onBack: () => void;
}

const QuizDetailsStep:FC<Props> = ({
  topic, setTopic,
  isAdvancedOpen, setIsAdvancedOpen,
  isDebugMode, setIsDebugMode,
  isScoringType,
  difficulty, setDifficulty,
  questionCount, changeQuestionCount,
  questionTimeout, changeQuestionTimeout,
  questionScore, changeQuestionScore,
  editablePrompt, setEditablePrompt, defaultPrompt,
  onNext, onBack,
}) => {
  const { t } = useTranslation();

  const topicRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-grow from a single line up to the max-h cap below, then let the
  // textarea's own overflow-y scroll take over instead of growing further.
  useEffect(() => {
    const el = topicRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [topic]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Mirror the Next button's own disabled condition — Enter shouldn't
      // be able to advance past validation the button itself blocks.
      if (topic.trim()) onNext();
    }
  }

  return (
    <div className="space-y-6">
      <div className="bg-[#2a2a2e] border border-gray-700 rounded-xl p-1 focus-within:border-orange-500 focus-within:ring-1 focus-within:ring-orange-500 transition-all">
        <textarea
          ref={topicRef}
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder={t('dialogs.quizAgent.topicPlaceholder')}
          className="w-full bg-transparent text-gray-100 placeholder-gray-500 outline-none p-4 text-lg resize-none overflow-y-auto max-h-32"
          onKeyDown={onKeyDown}
          rows={1}
          autoFocus
        />
      </div>
      <div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-200 transition-colors"
          >
            <Settings2 className="w-4 h-4" />
            {isAdvancedOpen ? t('dialogs.quizAgent.hideSettingsButton') : t('dialogs.quizAgent.showAdvancedSettingsButton')}
          </button>
          <button
            onClick={() => setIsDebugMode(!isDebugMode)}
            className={`flex items-center gap-2 text-sm transition-colors ${isDebugMode ? 'text-yellow-400 hover:text-yellow-300' : 'text-gray-600 hover:text-gray-400'}`}
            title={t('dialogs.quizAgent.debugModeTooltip')}
          >
            <Bug className="w-4 h-4" />
            {isDebugMode ? t('dialogs.quizAgent.debugOnButton') : t('dialogs.quizAgent.debugButton')}
          </button>
        </div>

        {isAdvancedOpen && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4 p-5 border rounded-xl" style={{ backgroundColor: 'rgba(42, 42, 46, 0.5)', borderColor: 'rgba(55, 65, 81, 0.5)' }}>
            {isScoringType && (
              <div className="space-y-2">
                <label className="text-sm text-gray-400">{t('dialogs.quizAgent.difficultyLevelLabel')}</label>
                <select
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value)}
                  className="w-full border border-gray-600 rounded-lg p-2.5 text-gray-200 outline-none focus:border-orange-500 appearance-none"
                  style={{ backgroundColor: '#1e1e24' }}
                >
                  {/* value stays English — difficulty feeds the agent prompt text verbatim, only the visible label is translated */}
                  <option value="Easy">{t('dialogs.quizAgent.difficultyEasy')}</option>
                  <option value="Medium">{t('dialogs.quizAgent.difficultyMedium')}</option>
                  <option value="Hard">{t('dialogs.quizAgent.difficultyHard')}</option>
                  <option value="Expert">{t('dialogs.quizAgent.difficultyExpert')}</option>
                </select>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm text-gray-400">{t('dialogs.quizAgent.numberOfQuestionsLabel')}</label>
              <input
                type="number"
                min="1"
                max="50"
                value={questionCount}
                onChange={changeQuestionCount}
                className="w-full border border-gray-600 rounded-lg p-2.5 text-gray-200 outline-none focus:border-orange-500"
                style={{ backgroundColor: '#1e1e24' }}
              />
            </div>
            {isScoringType && (
              <div className="space-y-2">
                <label className="text-sm text-gray-400">{t('dialogs.quizAgent.questionTimeoutLabel')}</label>
                <input
                  type="number"
                  min="1"
                  max="300"
                  value={questionTimeout}
                  onChange={changeQuestionTimeout}
                  className="w-full border border-gray-600 rounded-lg p-2.5 text-gray-200 outline-none focus:border-orange-500"
                  style={{ backgroundColor: '#1e1e24' }}
                />
              </div>
            )}
            {isScoringType && (
              <div className="space-y-2">
                <label className="text-sm text-gray-400">{t('dialogs.quizAgent.questionScoreLabel')}</label>
                <input
                  type="number"
                  min="0"
                  value={questionScore}
                  onChange={changeQuestionScore}
                  className="w-full border border-gray-600 rounded-lg p-2.5 text-gray-200 outline-none focus:border-orange-500"
                  style={{ backgroundColor: '#1e1e24' }}
                />
              </div>
            )}
          </div>
        )}

        {isDebugMode && (
          <div className="mt-4 p-5 border border-yellow-500/30 rounded-xl bg-yellow-500/5 space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-yellow-400 flex items-center gap-2">
                <Bug className="w-4 h-4" />
                {t('dialogs.quizAgent.builtInPromptLabel')}
              </label>
              <button
                onClick={() => setEditablePrompt(defaultPrompt)}
                className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
              >
                {t('dialogs.quizAgent.resetToDefaultButton')}
              </button>
            </div>
            <textarea
              value={editablePrompt}
              onChange={(e) => setEditablePrompt(e.target.value)}
              rows={10}
              className="w-full border border-yellow-500/30 rounded-lg p-3 text-sm text-gray-200 outline-none focus:border-yellow-500 font-mono resize-y"
              style={{ backgroundColor: '#1e1e24' }}
            />
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-8 py-3 rounded-xl font-medium text-gray-200 border border-gray-700 hover:bg-[#303035] transition-all"
        >
          <ChevronLeft className="w-5 h-5" />
          {t('buttons.back')}
        </button>
        <button
          onClick={onNext}
          disabled={!topic.trim()}
          className={`
            flex items-center gap-2 px-8 py-3 rounded-xl font-medium text-white transition-all
            ${!topic.trim()
              ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
              : 'bg-orange-600 hover:bg-orange-500 hover:shadow-lg hover:-translate-y-0.5'
            }
          `}
        >
          <span className="flex items-center gap-2">
            {t('buttons.next')}
            <ChevronRight className="w-5 h-5" />
          </span>
        </button>
      </div>
    </div>
  )
};

export default QuizDetailsStep;
